import React, { useState } from 'react'
import styles from '../IntroStep.module.scss'
import { AUDIT_TYPES } from '../../../services/dataAudit'

// The five types the app's own audit infers, so the "every field shows its
// detected type" claim is showing the real vocabulary rather than a made-up one.
const T = {
  text: AUDIT_TYPES.TEXT,
  categorical: AUDIT_TYPES.CATEGORICAL,
  numeric: AUDIT_TYPES.NUMERIC,
  datetime: AUDIT_TYPES.DATETIME,
  boolean: AUDIT_TYPES.BOOLEAN,
}

const VARIABLES = [
  { name: 'player', type: T.text },
  { name: 'team', type: T.categorical },
  { name: 'q', type: T.categorical },
  { name: 'shot_distance', type: T.numeric },
  { name: 'pts', type: T.numeric },
  { name: 'clock', type: T.datetime },
  { name: 'made', type: T.boolean },
]

// Beeswarm's slots. Only the axis is required, which is what makes the
// "1 of 1 required mapped" readout honest.
const SLOTS = [
  {
    key: 'X Axis',
    required: true,
    accepts: [T.numeric, T.datetime],
    hint: 'Accepts numeric or date fields.',
  },
  { key: 'Size', required: false, accepts: [T.numeric], hint: 'Accepts numeric fields.' },
  {
    key: 'Color',
    required: false,
    accepts: [T.categorical, T.text, T.numeric, T.boolean],
    hint: 'Accepts any field.',
  },
  {
    key: 'Label',
    required: false,
    accepts: [T.text, T.categorical, T.numeric],
    hint: 'Accepts text, category or numeric fields.',
  },
  {
    key: 'Groups',
    required: false,
    accepts: [T.categorical, T.text],
    hint: 'Accepts category or text fields.',
  },
]

const AUTO_MAPPED = { 'X Axis': 'shot_distance', Size: 'pts', Color: 'team' }
const INITIAL = { 'X Axis': 'shot_distance', Size: 'pts' }

const REQUIRED_TOTAL = SLOTS.filter((s) => s.required).length

export default function MappingPreview({ onSwitchChart }) {
  const [mapping, setMapping] = useState(INITIAL)
  const [openSlot, setOpenSlot] = useState(null)

  const active = SLOTS.find((s) => s.key === openSlot) || null
  const requiredDone = SLOTS.filter((s) => s.required && mapping[s.key]).length

  const assign = (slotKey, name) => {
    setMapping((prev) => ({ ...prev, [slotKey]: name }))
    setOpenSlot(null)
  }

  const clear = (slotKey) => {
    setMapping((prev) => {
      const next = { ...prev }
      delete next[slotKey]
      return next
    })
  }

  return (
    <div className={styles.mapCard}>
      <div
        className={styles.mapTrack}
        style={{ transform: active ? 'translateX(-50%)' : 'translateX(0)' }}
      >
        {/* Pane 1 — the mapping as it stands */}
        <div
          className={styles.mapPane}
          aria-hidden={active ? 'true' : 'false'}
          style={{ pointerEvents: active ? 'none' : 'auto' }}
        >
          <div className={styles.mapHead}>
            <span className={styles.mapHeadIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
                <circle cx="7" cy="9" r="1.4" />
                <circle cx="12" cy="6" r="1.4" />
                <circle cx="16" cy="11" r="1.4" />
                <circle cx="9" cy="15" r="1.4" />
                <circle cx="15" cy="17" r="1.4" />
              </svg>
            </span>
            <span className={styles.mapHeadText}>
              <span className={styles.mapEyebrow}>Selected chart</span>
              <span className={styles.mapChartName}>Beeswarm plot</span>
            </span>
            <button type="button" className={styles.mapSwitch} onClick={onSwitchChart}>
              Switch
            </button>
          </div>

          <div className={styles.mapStatusRow}>
            <span className={styles.mapStatus}>
              <strong>
                {requiredDone} of {REQUIRED_TOTAL}
              </strong>{' '}
              required mapped
            </span>
            <button
              type="button"
              className={styles.mapAutoBtn}
              onClick={() => {
                setMapping({ ...AUTO_MAPPED })
                setOpenSlot(null)
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
                <path d="M19 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
              </svg>
              Auto-map
            </button>
          </div>

          <ul className={styles.mapSlots}>
            {SLOTS.map((slot) => {
              const value = mapping[slot.key]
              return (
                <li key={slot.key} className={styles.mapSlotRow}>
                  <button
                    type="button"
                    className={styles.mapSlotBtn}
                    onClick={() => setOpenSlot(slot.key)}
                  >
                    <span className={value ? styles.mapDotOn : styles.mapDot} aria-hidden="true">
                      {value && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12.5l5 5L20 6.5" />
                        </svg>
                      )}
                    </span>
                    <span className={styles.mapSlotLabel}>{slot.key}</span>
                    {value ? (
                      <span className={styles.mapSlotValue}>{value}</span>
                    ) : (
                      <span className={styles.mapSlotHint}>
                        {slot.required ? 'Required' : 'Optional'} — tap to add
                      </span>
                    )}
                  </button>
                  {value && (
                    <button
                      type="button"
                      className={styles.mapClear}
                      aria-label={`Clear ${slot.key}`}
                      onClick={() => clear(slot.key)}
                    >
                      ×
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>

        {/* Pane 2 — the field picker for one slot */}
        <div
          className={styles.mapPane}
          aria-hidden={active ? 'false' : 'true'}
          style={{ pointerEvents: active ? 'auto' : 'none' }}
        >
          <div className={styles.mapPickerHead}>
            <button
              type="button"
              className={styles.mapBack}
              aria-label="Back to mapping"
              onClick={() => setOpenSlot(null)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 5l-7 7 7 7" />
              </svg>
            </button>
            <span className={styles.mapHeadText}>
              <span className={styles.mapEyebrow}>Map a field to</span>
              <span className={styles.mapChartName}>{active ? active.key : ''}</span>
            </span>
          </div>
          <p className={styles.mapPickerHint}>{active ? active.hint : ''}</p>

          <ul className={styles.mapVars}>
            {VARIABLES.map((v) => {
              const ok = active ? active.accepts.includes(v.type) : false
              return (
                <li key={v.name} className={styles.mapVarRow}>
                  <button
                    type="button"
                    className={styles.mapVarBtn}
                    disabled={!ok}
                    onClick={() => active && assign(active.key, v.name)}
                  >
                    <span className={styles.mapVarName}>{v.name}</span>
                    <span className={styles.mapVarType}>{v.type}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}
